import {classificationTexture} from './classifications';
import shaders from './shaders';
import {colorMaps} from '../textures/colormaps';
import {Viewer} from "./main";
import {
  Color, DataTexture, NearestFilter, DoubleSide,
  RGBAFormat, RGBFormat, ShaderMaterial,
  Uniform, Vector2, Vector3, TextureLoader
} from "three";
import {numberOfClassifications} from "../../classifications";

export class BaseMaterial extends ShaderMaterial {
  initProperties() {
    let keys = Object.keys(this.uniforms);
    keys.forEach(key => {
      this.defineProperty(key);
    });
  }

  defineProperty(key) {
    Object.defineProperty(this, key, {
      get: () => {
        return (this.uniforms[key].value);
      },
      set: value => {
        this.uniforms[key].value = value;
      }
    });
  }
}

const generateDataTexture = (width: number, height: number, channels, color: Color = new Color(0xffffff)) => {
  let size = width * height;
  let data = new Uint8Array(channels * width * height);

  let r = Math.floor(color.r * 255);
  let g = Math.floor(color.g * 255);
  let b = Math.floor(color.b * 255);

  for (let i = 0; i < size; i++) {
    data[i * channels] = r;
    data[i * channels + 1] = g;
    data[i * channels + 2] = b;

    if (channels === 4) {
      data[i * channels + 3] = 255;
    }
  }

  let format = channels === 3 ? RGBFormat : RGBAFormat;
  let texture = new DataTexture(data, width, height, format);
  texture.minFilter = NearestFilter;
  texture.magFilter = NearestFilter;
  texture.needsUpdate = true;

  return texture;
};

export interface PointCloudMaterialParams {
    minSize: number
    maxSize: number
    heightClamp: Vector2
    intensityClamp: Vector2
    dynamicSize: boolean
    circularPoints: boolean
    colorType: number
    distance: any
    size: number
    colorMap: string
    classification: object
}

export class PointCloudMaterial extends BaseMaterial {
    public size;
    public distance;
    public color_map;
    public color_type;
    public dynamic_size;
    public circularPoints;
    public visibleNodesTexture: any;
    public dynamic_cloud: boolean;
    public origin: any;
    public screenHeight: number;
    public screenwidth: number;
    public fov: number;
    public octree_spacing: any;
    public octree_size: number;
    public octree_corner: Vector3;
    public intensity_range: Vector2;
    public rgb_max: number;
    public height_range: Vector2;

    constructor(viewer: Viewer, params: PointCloudMaterialParams, gpuPicker) {
      super();

      // Node depth texture for dynamic sizing
      this.visibleNodesTexture = generateDataTexture(2048, 1, 3);

      // Load colormap texture
      const loader = new TextureLoader();
      const colorMapTexture = loader.load(colorMaps.find(colorMap => colorMap.id === params.colorMap).image);

      // Generate classification visibility array
      const classificationVisible = new Array(numberOfClassifications).fill(true);
      classificationTexture.minFilter = NearestFilter;
      classificationTexture.magFilter = NearestFilter;

      // Initialize area data
      const maxTriangles = 256;
      let maxAreaTriangles = maxTriangles;
      let maxAreaVertices = maxTriangles * 3;
      let areaClipData = new Float32Array(maxAreaTriangles * 3 * 3).fill(0.0);
      let volumeStatus = new Float32Array(maxAreaTriangles).fill(0.0);

      let uniforms = {
        gpuPicker: new Uniform(gpuPicker),
        circularPoints: new Uniform(params.circularPoints),
        "depth": new Uniform(1.0),
        "sphere": new Uniform(viewer.camera.position),
        "origin": new Uniform(viewer.camera.position),
        "size": new Uniform(params.size),
        "size_min": new Uniform(params.minSize),
        "size_max": new Uniform(params.maxSize),
        "fov": new Uniform(viewer.camera.fov * Math.PI / 180.0),
        "screenHeight": new Uniform(viewer.height),
        "screenwidth": new Uniform(viewer.width),
        "distance": new Uniform(params.distance),
        "octree_size": new Uniform(0.0),
        "octree_spacing": new Uniform(0.0),
        "octree_corner": new Uniform(viewer.camera.position),
        "color_type": new Uniform(params.colorType),
        "height_range": new Uniform(new Vector2(0, 1)),
        "height_clamp": new Uniform(params.heightClamp),
        "intensity_range": new Uniform(new Vector2(0, 1)),
        "intensity_clamp": new Uniform(params.intensityClamp),
        "rgb_max": new Uniform(0),
        "color_map": new Uniform(colorMapTexture),
        "dynamic_size": new Uniform(params.dynamicSize),
        "dynamic_cloud": new Uniform(false),
        "classification_data": new Uniform(classificationTexture),
        "classification_visible": new Uniform(classificationVisible),
        "visible_nodes": new Uniform(this.visibleNodesTexture),
        "volume_status": new Uniform(volumeStatus),
        "max_triangles": new Uniform(maxTriangles),
        "area_measure_data": new Uniform(areaClipData)
      };

      let defines = [
        `#define NUMBER_OF_CLASSIFICATIONS ${numberOfClassifications}`,
        `#define AREA_TRIANGLES_TOTAL ${maxAreaTriangles}`,
        `#define AREA_VERTICES_TOTAL ${maxAreaVertices}`
      ].join("\n");

      let fragmentShader = shaders.pointcloud.frag;

      let vertexShader = [
        defines,
        shaders.gpu_picker.frag,
        shaders.utilities.vert,
        shaders.pointcloud.vert
      ].join("\n");

      this.setValues({
        uniforms: uniforms,
        vertexShader: vertexShader,
        fragmentShader: fragmentShader,
        vertexColors: true,
        transparent: true,
        depthWrite: true
      });

      this.initProperties();
    }
}

export class EDLMaterial extends BaseMaterial {
    public screenWidth: any;
    public screenHeight: any;
    public edlStrength: any;

    constructor(parent) {
      super();

      let vertexShader = shaders.edl.vert;
      let fragmentShader = shaders.edl.frag;

      let neighbourCount = 4;
      let neighbours = new Float32Array(neighbourCount * 2);
      for (let c = 0; c < neighbourCount; c++) {
        neighbours[2 * c] = Math.cos(2 * c * Math.PI / neighbourCount);
        neighbours[2 * c + 1] = Math.sin(2 * c * Math.PI / neighbourCount);
      }

      let lightDir = new Vector3(0.0, 0.0, 1.0).normalize();
      let uniform = {
        screenWidth: {type: "f", value: parent.WIDTH},
        screenHeight: {type: "f", value: parent.HEIGHT},
        near: {type: "f", value: parent.camera.near},
        far: {type: "f", value: parent.camera.far},
        opacity: {type: "f", value: 1.0},
        expScale: {type: "f", value: parent.camera.far},
        edlStrength: {type: "f", value: 1.0},
        radius: {type: "f", value: 1.0},
        lightDir: {type: "v3", value: lightDir},
        neighbours: {type: "2fv", value: neighbours},
        colorMap: {type: "t", value: null},
        tDepth: {type: "t", value: null}
      };

      this.setValues({
        uniforms: uniform,
        vertexShader: vertexShader,
        fragmentShader: fragmentShader,
        transparent: true
      });

      this.initProperties();
    }
}

export class OpacityMaterial extends BaseMaterial {
  constructor() {
    super();

    let vertexShader = shaders.opacity.vert;
    let fragmentShader = shaders.opacity.frag;

    let uniform = {
      "colorMap": {type: "t", value: null},
      "opacity": {type: "f", value: 1.0}
    };

    this.setValues({
      uniforms: uniform,
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      transparent: true
    });

    this.initProperties();
  }
}

export class ImageMaterial extends BaseMaterial {
  constructor() {
    super();

    let vertexShader = shaders.image.vert;
    let fragmentShader = shaders.image.frag;

    let uniform = {
      "colorMap": {
        type: "t",
        value: null
      },
      "brightness": {
        type: "f",
        value: 0
      },
      "contrast": {
        type: "f",
        value: 0
      }
    };

    this.setValues({
      uniforms: uniform,
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      side: DoubleSide
    });

    this.initProperties();
  }
}

export class MarkerMaterial extends BaseMaterial {
  constructor(viewer: Viewer, gpuPicker) {
    super();

    let fragmentShader = shaders.markers.frag;

    let vertexShader = [
      shaders.gpu_picker.frag,
      shaders.utilities.vert,
      shaders.markers.vert
    ].join("\n");

    let uniform = {
      gpuPicker: new Uniform(gpuPicker),
      orbitMode: new Uniform(false),
      camera_position: new Uniform(new Vector3(0, 1, 0)),
      mtrx_x: new Uniform(new Vector3(1, 0, 0)),
      mtrx_y: new Uniform(new Vector3(0, 1, 0)),
      mtrx_z: new Uniform(new Vector3(0, 0, 1)),
      marker_size: new Uniform(1.0),
      fov: new Uniform(viewer.camera.fov * Math.PI / 180.0),
      screenHeight: new Uniform(viewer.height),
      currentIndex: new Uniform(0)
    };

    this.setValues({
      uniforms: uniform,
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      side: DoubleSide
    });

    this.initProperties();
  }
}